import { useEffect, useRef, useState } from "react";
import config from "../../config";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Fab,
  Snackbar,
  Alert,
  keyframes,
} from "@mui/material";
import {
  SmartToy as AIIcon,
} from "@mui/icons-material";
import Utilities from "../../utils/utilities";
import { useTheme } from "@mui/material/styles";
import useAuth from "../../hooks/useAuth";
import AiService from "../../services/AiService";
import { useTranslation } from "react-i18next";
import AiAnalysisDialog from "./aiAnalysisDialog";

// Animazioni keyframes
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 130, 130, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(0, 130, 130, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 130, 130, 0);
  }
`;

const breatheAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const glowAnimation = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px rgba(0, 130, 130, 0.5), 0 0 10px rgba(0, 130, 130, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(0, 130, 130, 0.8), 0 0 30px rgba(0, 130, 130, 0.6);
  }
`;

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const GrafanaDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [grafanaUrl, setGrafanaUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const utilities = new Utilities();
  const aiService = new AiService();
  const user = useAuth();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Stati per l'AI
  const [aiDialogOpen, setAiDialogOpen] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiContent, setAiContent] = useState<string>("");
  const [aiError, setAiError] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  // Stato per l'animazione di attrazione
  const [showAttentionAnimation, setShowAttentionAnimation] = useState<boolean>(false);

  // Recupera l'assetId dalla query string
  const assetId = searchParams.get("id");

  useEffect(() => {
    const fetchGrafanaUrl = async (): Promise<void> => {
      try {
        const dashboardUrl = `${config.grafanaConfig.url}/d/${
          config.grafanaConfig.dashboardId
        }/${
          config.grafanaConfig.dashboardName
        }/?kiosk&theme=${utilities.getThemeColorByUser(
          theme.palette.mode
        )}&var-assetId=${encodeURIComponent(
          assetId!
        )}&var-userId=${user?.user?.userId?.toUpperCase()}`;

        setGrafanaUrl(dashboardUrl);
      } catch (error) {
        console.error("Error fetching Grafana token:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.user?.userId && assetId) {
      fetchGrafanaUrl();
    }
  }, [theme.palette.mode, assetId, user]);

  // Effetto per mostrare l'animazione di attrazione dopo un po' di tempo
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!loading && grafanaUrl) {
      // Mostra l'animazione dopo 3 secondi
      const timer1 = setTimeout(() => {
        setShowAttentionAnimation(true);
      }, 3000);

      // Ripete l'animazione ogni 15 secondi
      const timer2 = setInterval(() => {
        setShowAttentionAnimation(true);
        setTimeout(() => setShowAttentionAnimation(false), 2000);
      }, 15000);

      return () => {
        clearTimeout(timer1);
        clearInterval(timer2);
      };
    }
  }, [loading, grafanaUrl]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (aiDialogOpen) {
          setAiDialogOpen(false);
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // eslint-disable-next-line consistent-return
        return false;
      }
      // eslint-disable-next-line consistent-return
      return true;
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [aiDialogOpen]);

  useEffect(() => {
    const handleFocus = () => {
      if (overlayRef.current) {
        overlayRef.current.focus();
      }
    };

    const interval = setInterval(() => {
      const iframe = document.querySelector(
        'iframe[title="g-dashboard"]'
      ) as HTMLIFrameElement;
      if (iframe && document.activeElement === iframe) {
        handleFocus();
      }
    }, 100);

    return () => clearInterval(interval);
  }, [grafanaUrl]);

  // Funzione per chiamare l'API AI
  const handleAiAnalysis = async () => {
    // Ferma l'animazione di attrazione quando l'utente clicca
    setShowAttentionAnimation(false);

    if (!assetId) {
      setAiError(t('GRAFANA_AI_ERROR_ASSET_ID_NOT_AVAILABLE'));
      setSnackbarOpen(true);
      return;
    }

    // Se abbiamo già il contenuto, apri solo la dialog
    if (aiContent && !aiError) {
      setAiDialogOpen(true);
      return;
    }

    setAiLoading(true);
    setAiError("");
    setAiDialogOpen(true);

    try {
      const htmlContent = await aiService.getSummaryData(assetId);
      setAiContent(htmlContent);
    } catch (error) {
      console.error("Errore nella chiamata AI:", error);
      setAiError(
        error instanceof Error
          ? error.message
          : t('GRAFANA_AI_ERROR_UNKNOWN')
      );
    } finally {
      setAiLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setAiDialogOpen(false);
    setAiError("");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 110px)",
        position: "relative",
      }}
    >
      {loading && <CircularProgress size={24} />}

      {!loading && grafanaUrl && (
        <>
          <iframe
            title="g-dashboard"
            src={grafanaUrl}
            width="100%"
            height="100%"
            sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups allow-downloads"
            referrerPolicy="strict-origin-when-cross-origin"
            style={{
              border: "none",
              margin: "0px 0px 0px 0px",
            }}
          />

          {/* Overlay per gestire focus */}
          <Box
            ref={overlayRef}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
              pointerEvents: "none",
              outline: "none",
              zIndex: 999,
            }}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />

          {/* Bottone AI flottante con animazioni */}
          <Fab
            color="primary"
            aria-label={t('GRAFANA_AI_BUTTON_ARIA_LABEL')}
            onClick={handleAiAnalysis}
            disabled={aiLoading}
            sx={{
              position: "absolute",
              bottom: 20,
              right: 0,
              zIndex: 10000,
              background:
                "linear-gradient(135deg, rgb(0, 130, 130) 0%, rgba(0, 130, 130, 0.8) 100%)",

              // Animazioni di base
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgb(0, 110, 110) 0%, rgba(0, 110, 110, 0.9) 100%)",
                transform: "scale(1.1) rotate(5deg)",
                boxShadow: "0 8px 25px rgba(0, 130, 130, 0.4)",
              },

              "&:disabled": {
                background: "rgba(0,0,0,0.26)",
              },

              // Transizione base
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

              // Animazione di respiro costante
              animation: `${breatheAnimation} 4s ease-in-out infinite`,

              // Animazioni di attrazione quando showAttentionAnimation è true
              ...(showAttentionAnimation && {
                animation: `
                  ${pulseAnimation} 2s ease-in-out infinite,
                  ${glowAnimation} 2s ease-in-out infinite,
                  ${bounceAnimation} 1s ease-in-out 3
                `,
              }),

              // Effetto glow costante
              filter: "drop-shadow(0 0 8px rgba(0, 130, 130, 0.3))",

              // Animazione dell'icona
              "& .MuiSvgIcon-root": {
                transition: "transform 0.3s ease",
                animation: showAttentionAnimation
                  ? `${keyframes`
                      0%, 100% { transform: rotate(0deg); }
                      25% { transform: rotate(-10deg); }
                      75% { transform: rotate(10deg); }
                    `} 0.5s ease-in-out infinite`
                  : "none",
              },

              "&:hover .MuiSvgIcon-root": {
                transform: "scale(1.2)",
              },
            }}
          >
            {aiLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              <AIIcon sx={{ color: "white" }} />
            )}
          </Fab>
        </>
      )}

      {/* Componente Dialog AI */}
      <AiAnalysisDialog
        open={aiDialogOpen}
        onClose={handleCloseDialog}
        loading={aiLoading}
        content={aiContent}
        error={aiError}
        onRetry={handleAiAnalysis}
        assetId={assetId}
      />

      {/* Snackbar per errori */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {aiError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GrafanaDashboard;
